import { useEffect, useState } from 'react';
// material-ui
import { Grid, Skeleton, Stack, Button, Box } from '@mui/material';
import useToken from 'services/useToken';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { getUserDevices } from 'services/apiService';
import MainCard from 'components/MainCard';
import { getDeviceStatus } from 'services/FirestoreDataService';
import { fDateTime } from '../../utils/format-time';

const AdminStatsTable = ({ appUser }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useToken();

  const getActivityStatus = (last_online_time) => {
    const offlineThreshold = 5 * 60 * 1000; // 5 minutes;
    const currentTime = Date.now();
    return currentTime - last_online_time < offlineThreshold;
  };

  const formatUserType = (user_type) => {
    switch (user_type) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'ORG_ADMIN':
        return 'Org Admin';
      case 'DEVICE_USER':
        return 'Device User';
      default:
        return '-';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const deviceList = await getUserDevices(token);
        console.log('device list', deviceList);
        const promises = deviceList.data.map(async (device) => {
          const deviceStatus = await getDeviceStatus(device.IMEI);
          const statusInfo = deviceStatus.exists() ? deviceStatus.data() : null;
          return { IMEI: device.IMEI, deviceInfo: device, statusInfo };
        });

        const combinedDeviceInfo = await Promise.all(promises);
        console.log('combined device status info', combinedDeviceInfo);

        const processedRows = combinedDeviceInfo.map((device, index) => {
          return {
            id: index,
            IMEI: device.IMEI,
            model: device.deviceInfo?.model ? device.deviceInfo?.model : 'N.A',
            device_name: device.deviceInfo?.device_name
              ? device.deviceInfo?.device_name
              : device.deviceInfo?.created_by_user
              ? device.deviceInfo?.created_by_user
              : '-',
            email: device.deviceInfo?.email ? device.deviceInfo?.email : '-',
            user_type: device.deviceInfo?.user_type ? formatUserType(device.deviceInfo?.user_type) : '-',
            status: getActivityStatus(device.statusInfo?.last_online_time) ? 'Online' : 'Offline',
            last_online_time: device.statusInfo?.last_online_time ? fDateTime(device.statusInfo?.last_online_time) : '-'
          };
        });
        setRows(processedRows);
      } catch (error) {
        console.error('Error fetching device data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]); // Re-fetch data when token changes (if applicable)

  const columns = [
    { field: 'IMEI', headerName: 'IMEI', width: 200, sortable: true },
    { field: 'model', headerName: 'Device Model', width: 150, sortable: true },
    { field: 'device_name', headerName: 'Device Name / User Name', width: 250, sortable: true },
    { field: 'email', headerName: 'Email', width: 250, sortable: true },
    { field: 'user_type', headerName: 'User Type', width: 150, sortable: true },
    { field: 'status', headerName: 'Status', width: 150, sortable: true },
    { field: 'last_online_time', headerName: 'Last Online Time', width: 200, sortable: true }
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {loading ? ( // Display loading indicator while data is being fetched
          <Skeleton variant="rectangular" width={600} height={300} />
        ) : (
          <MainCard sx={{ height: 520, width: '172%' }}>
            <DataGrid
              style={{ height: 440, width: '100%' }} // Set height and overflow
              rows={rows}
              columns={columns}
              isLoading={loading} // Set loading state for DataGrid
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              initialState={{
                filter: {
                  filterModel: {
                    items: []
                  }
                },
                pagination: {
                  paginationModel: {
                    pageSize: 25
                  }
                }
              }}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: false,
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true }
                }
              }}
              sx={{
                mt: 2,
                fontSize: 14,
                borderColor: 'secondary.light',
                '& .MuiDataGrid-row:hover': {
                  bgcolor: 'primary.lighter'
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: 'primary.lighter'
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0
                },
                '& .MuiDataGrid-withBorderColor': {
                  borderColor: 'secondary.light'
                },
                // **Mobile responsiveness changes:**
                '@media (max-width: 600px)': {
                  '& .MuiDataGrid-headerCell': {
                    padding: '4px 8px !important'
                  },
                  '& .MuiDataGrid-toolbarContainer': {
                    justifyContent: 'flex-start !important'
                  }
                }
              }}
            />
          </MainCard>
        )}
      </Grid>
    </Grid>
  );
};

AdminStatsTable.propTypes = {};

export default AdminStatsTable;
